/**
 * Navigation V2 - Theme Variation Based Navigation
 * Handles mobile menu, dropdowns, and theme-specific behaviors
 */

class NavigationV2 {
    constructor() {
        this.nav = document.querySelector('.nav-v2');
        if (!this.nav) return;

        this.theme = this.nav.dataset.theme || 'default';
        this.mobileMenu = this.nav.querySelector('[data-mobile-menu]');
        this.navToggle = this.nav.querySelector('[data-nav-toggle]');
        this.hamburgerMenu = document.getElementById('hamburger-menu');
        this.navOverlay = this.nav.querySelector('[data-nav-overlay]');
        this.navPanel = this.nav.querySelector('[data-nav-panel]');
        this.openText = this.nav.querySelector('[data-nav-open-text]');
        this.closeText = this.nav.querySelector('[data-nav-close-text]');
        
        this.isOpen = false;
        
        this.init();
    }

    init() {
        console.log('NavigationV2 init:', {
            nav: this.nav,
            mobileMenu: this.mobileMenu,
            navPanel: this.navPanel,
            navToggle: this.navToggle
        });

        // Mobile menu toggle
        if (this.navToggle) {
            this.navToggle.addEventListener('click', () => this.toggleMobileMenu());
            
            // Keyboard support for hamburger menu
            this.navToggle.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.toggleMobileMenu();
                }
            });
        }

        // Use event delegation for ALL clicks within the nav panel
        document.addEventListener('click', (e) => {
            // Handle close button clicks
            if (e.target.closest('[data-nav-close]')) {
                e.preventDefault();
                this.closeMobileMenu();
                return;
            }

            // Handle submenu toggle clicks
            const toggle = e.target.closest('[data-submenu-toggle]');
            if (toggle && this.navPanel && this.navPanel.contains(toggle)) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleSubmenu(toggle);
                return;
            }
        });

        // Overlay click
        if (this.navOverlay) {
            this.navOverlay.addEventListener('click', () => this.closeMobileMenu());
        }

        // Handle escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeMobileMenu();
            }
        });

        // Handle window resize
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                if (window.innerWidth >= 768 && this.isOpen) {
                    this.closeMobileMenu();
                }
            }, 250);
        });

        // Theme-specific initializations
        this.initThemeSpecific();
    }

    toggleMobileMenu() {
        this.isOpen ? this.closeMobileMenu() : this.openMobileMenu();
    }

    openMobileMenu() {
        if (!this.mobileMenu || !this.navPanel) {
            console.error('Mobile menu elements not found');
            return;
        }

        this.isOpen = true;
        this.mobileMenu.classList.remove('hidden');
        
        // Force reflow to ensure transition works
        this.mobileMenu.offsetHeight;
        
        // Animate panel
        this.navPanel.classList.remove('translate-x-full');
        
        // Update toggle button
        if (this.navToggle) {
            this.navToggle.setAttribute('aria-expanded', 'true');
        }
        // Update hamburger menu animation
        if (this.hamburgerMenu) {
            this.hamburgerMenu.classList.add('active');
        }
        if (this.openText) {
            this.openText.classList.add('hidden');
        }
        if (this.closeText) {
            this.closeText.classList.remove('hidden');
        }
        
        // Prevent body scroll without shifting content
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = scrollBarWidth + 'px';
        
        // Focus management - find close button inside panel
        const navClose = this.navPanel.querySelector('[data-nav-close]');
        if (navClose) {
            navClose.focus();
        }
        
        // Trap focus
        this.trapFocus();
    }

    closeMobileMenu() {
        if (!this.mobileMenu || !this.navPanel) return;

        this.isOpen = false;
        
        // Animate panel
        this.navPanel.classList.add('translate-x-full');
        
        // Wait for transition to complete before hiding
        this.navPanel.addEventListener('transitionend', function hideMenu() {
            // Only hide if the panel is still translated (menu is closed)
            if (this.navPanel.classList.contains('translate-x-full')) {
                this.mobileMenu.classList.add('hidden');
            }
            // Remove the event listener after it runs
            this.navPanel.removeEventListener('transitionend', hideMenu);
        }.bind(this));
        
        // Update toggle button
        if (this.navToggle) {
            this.navToggle.setAttribute('aria-expanded', 'false');
        }
        // Update hamburger menu animation
        if (this.hamburgerMenu) {
            this.hamburgerMenu.classList.remove('active');
        }
        if (this.openText) {
            this.openText.classList.remove('hidden');
        }
        if (this.closeText) {
            this.closeText.classList.add('hidden');
        }
        
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        
        // Return focus
        if (this.navToggle) {
            this.navToggle.focus();
        }
        
        // Remove focus trap
        this.removeFocusTrap();
    }

    initSubmenuToggles() {
        // Use event delegation for submenu toggles
        if (this.navPanel) {
            this.navPanel.addEventListener('click', (e) => {
                console.log('Click in nav panel:', e.target);
                const toggle = e.target.closest('[data-submenu-toggle]');
                console.log('Found toggle:', toggle);
                if (toggle) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleSubmenu(toggle);
                }
            });
        } else {
            console.error('NavPanel not found for submenu toggles');
        }
    }

    toggleSubmenu(toggle) {
        const submenuId = toggle.dataset.submenuToggle;
        // Try to find submenu in the document since it might be dynamically added
        const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);
        const icon = toggle.querySelector('[data-submenu-icon]');
        
        console.log('Toggle submenu:', {
            submenuId,
            submenu,
            toggle,
            icon,
            navPanel: this.navPanel
        });
        
        if (!submenu) {
            console.error('Submenu not found for ID:', submenuId);
            // List all submenus found
            const allSubmenus = document.querySelectorAll('[data-submenu]');
            console.log('Available submenus:', allSubmenus);
            return;
        }
        
        const isOpen = !submenu.classList.contains('hidden');
        
        // Get theme-specific background class
        const theme = this.nav.dataset.theme || 'default';
        const isDarkHeader = document.body.classList.contains('dark-header') || 
                            this.nav.closest('.header-dark') !== null;
        
        let bgClass = '';
        if (theme === 'edge') {
            bgClass = 'bg-[#192938]';
        } else if (theme === 'default' && isDarkHeader) {
            bgClass = 'bg-[#E6EAFA]';
        } else {
            bgClass = 'bg-[#2B20A8]';
        }
        
        if (isOpen) {
            // Close submenu
            submenu.classList.add('hidden');
            if (icon) icon.classList.remove('rotate-180');
            toggle.setAttribute('aria-expanded', 'false');
            
            // Remove background from button
            toggle.classList.remove(bgClass);
        } else {
            // Close all other open submenus first
            const allSubmenus = document.querySelectorAll('[data-submenu]');
            const allToggles = document.querySelectorAll('[data-submenu-toggle]');
            
            allSubmenus.forEach((otherSubmenu, index) => {
                if (otherSubmenu !== submenu && !otherSubmenu.classList.contains('hidden')) {
                    otherSubmenu.classList.add('hidden');
                    
                    // Update the corresponding toggle button
                    const otherToggle = allToggles[index];
                    if (otherToggle) {
                        const otherIcon = otherToggle.querySelector('[data-submenu-icon]');
                        if (otherIcon) otherIcon.classList.remove('rotate-180');
                        otherToggle.setAttribute('aria-expanded', 'false');
                        
                        // Remove all possible background classes
                        otherToggle.classList.remove('bg-[#192938]', 'bg-[#E6EAFA]', 'bg-[#2B20A8]');
                    }
                }
            });
            
            // Open submenu
            submenu.classList.remove('hidden');
            if (icon) icon.classList.add('rotate-180');
            toggle.setAttribute('aria-expanded', 'true');
            
            // Add background to button
            toggle.classList.add(bgClass);
        }
    }

    trapFocus() {
        if (!this.navPanel) return;
        
        const focusableElements = this.navPanel.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
        );
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        
        this.handleFocusTrap = (e) => {
            if (e.key !== 'Tab') return;
            
            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        };
        
        document.addEventListener('keydown', this.handleFocusTrap);
    }

    removeFocusTrap() {
        if (this.handleFocusTrap) {
            document.removeEventListener('keydown', this.handleFocusTrap);
        }
    }

    initThemeSpecific() {
        // Add theme-specific behaviors here
        switch(this.theme) {
            case 'edge':
                this.initEdgeTheme();
                break;
            case 'modern':
                this.initModernTheme();
                break;
            default:
                this.initDefaultTheme();
        }
    }

    initDefaultTheme() {
        // Default theme specific initializations
    }

    initEdgeTheme() {
        // Edge theme specific initializations
        // For example: add subtle animations on scroll
        let lastScroll = 0;
        window.addEventListener('scroll', () => {
            const currentScroll = window.pageYOffset;
            
            if (currentScroll > lastScroll && currentScroll > 100) {
                // Scrolling down
                this.nav.classList.add('shadow-lg');
            } else {
                // Scrolling up
                this.nav.classList.remove('shadow-lg');
            }
            
            lastScroll = currentScroll;
        });
    }

    initModernTheme() {
        // Modern theme specific initializations
        // For example: add gradient animations
        if (window.innerWidth >= 768) {
            this.nav.addEventListener('mousemove', (e) => {
                const rect = this.nav.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                this.nav.style.setProperty('--gradient-x', `${x}%`);
            });
        }
    }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new NavigationV2());
} else {
    new NavigationV2();
}

export default NavigationV2;